import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import { SubstitutionRulesTable } from 'components/fbm/substitutions/SubstitutionRulesTable';
import { UserContext } from 'contexts/UserContext';
import { useDeleteRuleGroupMutation } from 'shared/hooks/fbm/useDeleteRuleGroupMutation';
import { useSubstitutionRulesQuery } from 'shared/hooks/fbm/useSubstitutionRulesQuery';
import { useToggleRuleFacilityStatusMutation } from 'shared/hooks/fbm/useToggleRuleFacilityStatusMutation';

function ViewSubstitutionRules() {
    const { fbmAccount, user } = useContext(UserContext);
    const fbmAccountName = fbmAccount?.fbmAccountName;
    const facilityId = user?.facilityID;
    const facilityName = user?.facilityName;

    const { data, error, isFetching, isLoading } = useSubstitutionRulesQuery({
        enabled: !!fbmAccount?.id,
        fbmAccountName,
        facilityId,
    });

    const rows = useMemo(() => data ?? [], [data]);

    useEffect(() => {
        if (error) {
            toast.error(`No substitution rules found: ${error.message}`);
        }
    }, [error]);

    const { mutate: toggleRuleFacilityStatus } = useToggleRuleFacilityStatusMutation({
        onError: (toggleError) => {
            toast.error(toggleError.message ?? 'Failed to update substitution rule');
        },
        onSuccess: () => {
            toast.success('Substitution rule updated successfully');
        },
    });

    const handleToggleEnabled = useCallback(
        (rule) => {
            if (!facilityId) {
                toast.error('Please select a facility to toggle substitution rules');
                return;
            }

            if (!rule?.ruleGroupId) {
                toast.error('Unable to toggle rule: missing rule group ID');
                return;
            }

            const isEnabled = rule.isEnabled !== false;

            toggleRuleFacilityStatus({
                fbmAccountName,
                facilityId,
                isEnabled: !isEnabled,
                ruleGroupId: rule.ruleGroupId,
            });
        },
        [facilityId, fbmAccountName, toggleRuleFacilityStatus],
    );

    const { mutate: deleteRuleGroup } = useDeleteRuleGroupMutation({
        onError: (deleteError) => {
            toast.error(deleteError.message ?? 'Failed to delete substitution rule');
        },
        onSuccess: () => {
            toast.success('Substitution rule deleted successfully');
        },
    });

    const handleDeleteRule = useCallback(
        (rule) => {
            if (!rule?.ruleGroupId) {
                toast.error('Unable to delete rule: missing rule group ID');
                return;
            }

            deleteRuleGroup({
                fbmAccountName,
                facilityId,
                ruleGroupId: rule.ruleGroupId,
            });
        },
        [deleteRuleGroup, facilityId, fbmAccountName],
    );

    const title = fbmAccount?.companyName ? `${fbmAccount.companyName} - Substitution Rules` : 'Substitution Rules';

    if (!fbmAccountName) {
        return (
            <div className="mt-4">
                <h2 className="mb-4 text-lg font-semibold">{title}</h2>
                <p className="text-sm text-muted-foreground">
                    Please select an FBM account above to view substitution rules.
                </p>
            </div>
        );
    }

    return (
        <div className="mt-4 flex flex-col">
            <h2 className="mb-4 text-lg font-semibold">{title}</h2>
            <SubstitutionRulesTable
                data={rows}
                facilityName={facilityName}
                isLoading={isLoading}
                isFetching={isFetching}
                onToggleEnabled={handleToggleEnabled}
                onDelete={handleDeleteRule}
            />
        </div>
    );
}

export { ViewSubstitutionRules };
